/**
 * The invite rows the Users page shows next to real users. One row per email,
 * the newest invite, skipped when that invite was accepted or revoked or when
 * a real user with the email already exists. Whether the row reads Invited or
 * Expired comes from the date (`shared/inviteExpiry.ts`), never from a flag
 * someone has to keep current: an aged-out invite stays `pending` on disk, so
 * it would otherwise sit in the list as "Invited" forever, and hiding it
 * instead would make a link the admin created vanish without a word.
 */
import type { Invite, OrgUser } from '../types'
import { inviteExpiresAtMs, isInviteExpired } from '../../shared/inviteExpiry'

export type InviteRowState = 'invited' | 'expired'

export interface InviteRow {
  invite: Invite
  state: InviteRowState
}

const normalizeEmail = (email: string | null | undefined) => email?.toLowerCase().trim() ?? ''

export function buildInviteRows(invites: Invite[], users: OrgUser[], nowMs: number = Date.now()): InviteRow[] {
  const userEmails = new Set(users.map(u => normalizeEmail(u.email)).filter(Boolean))

  const newestByEmail = new Map<string, Invite>()
  for (const inv of invites) {
    const key = normalizeEmail(inv.email)
    const current = newestByEmail.get(key)
    if (!current || inv.createdAt > current.createdAt) newestByEmail.set(key, inv)
  }

  const rows: InviteRow[] = []
  for (const [email, inv] of newestByEmail) {
    if (inv.status === 'accepted' || inv.status === 'revoked') continue
    if (userEmails.has(email)) continue
    const expired = inv.status === 'expired' || isInviteExpired(inv.expiresAt, nowMs)
    rows.push({ invite: inv, state: expired ? 'expired' : 'invited' })
  }
  return rows
}

/** The badge's hover text: "expires Oct 8" on a live row, "expired Oct 8" on a
 *  dead one; null when the date cannot be read. */
export function inviteExpiryLabel(
  invite: Pick<Invite, 'expiresAt'>,
  state: InviteRowState,
  locale?: string,
): string | null {
  const ms = inviteExpiresAtMs(invite.expiresAt)
  if (ms === null) return null
  const day = new Date(ms).toLocaleDateString(locale, { month: 'short', day: 'numeric' })
  return `${state === 'expired' ? 'expired' : 'expires'} ${day}`
}
